import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Button } from '../atoms/Button'

/**
 * DataTablePagination — prev/next + page indicator + page size.
 */
export interface DataTablePaginationProps extends React.HTMLAttributes<HTMLDivElement> {
  page: number
  pageCount: number
  pageSize?: number
  total?: number
  onPageChange: (page: number) => void
  pageSizes?: number[]
  onPageSizeChange?: (size: number) => void
}

export const DataTablePagination = React.forwardRef<HTMLDivElement, DataTablePaginationProps>(
  (
    {
      className,
      page,
      pageCount,
      pageSize = 20,
      total,
      onPageChange,
      pageSizes = [10, 20, 50, 100],
      onPageSizeChange,
      ...props
    },
    ref,
  ) => {
    const isEmpty = total !== undefined && total === 0
    const canPrev = page > 1
    const canNext = page < pageCount
    const firstRow = isEmpty ? 0 : (page - 1) * pageSize + 1
    const lastRow = isEmpty ? 0 : Math.min(page * pageSize, total ?? page * pageSize)

    // When empty, show just "0 results" with page size selector — no nav buttons
    if (isEmpty) {
      return (
        <div
          ref={ref}
          className={cn(
            'flex items-center gap-3 py-3 text-body-sm text-ia-text-tertiary',
            className,
          )}
          {...props}
        >
          <span>0 results</span>
          {onPageSizeChange && (
            <label className="flex items-center gap-1">
              Rows
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="h-8 rounded-md border border-ia-border bg-ia-input-bg px-2 text-body-sm text-ia-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {pageSizes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
      )
    }

    return (
      <div
        ref={ref}
        className={cn(
          'flex items-center justify-between gap-4 py-3 text-body-sm text-ia-text-secondary',
          className,
        )}
        {...props}
      >
        <div className="flex items-center gap-3">
          {total !== undefined && (
            <span>
              {firstRow}–{lastRow} of{' '}
              <span className="font-medium text-ia-text tabular-nums">{total}</span>
            </span>
          )}
          {onPageSizeChange && (
            <label className="flex items-center gap-1">
              Rows
              <select
                value={pageSize}
                onChange={(e) => onPageSizeChange(Number(e.target.value))}
                className="h-8 rounded-md border border-ia-border bg-ia-input-bg px-2 text-body-sm text-ia-text focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
              >
                {pageSizes.map((s) => (
                  <option key={s} value={s}>
                    {s}
                  </option>
                ))}
              </select>
            </label>
          )}
        </div>
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            disabled={!canPrev}
            onClick={() => onPageChange(1)}
            aria-label="First page"
          >
            <ChevronsLeft />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={!canPrev}
            onClick={() => onPageChange(page - 1)}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </Button>
          <span className="text-body-sm text-ia-text tabular-nums px-3">
            Page <span className="font-medium">{page}</span> of {pageCount}
          </span>
          <Button
            size="icon"
            variant="ghost"
            disabled={!canNext}
            onClick={() => onPageChange(page + 1)}
            aria-label="Next page"
          >
            <ChevronRight />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            disabled={!canNext}
            onClick={() => onPageChange(pageCount)}
            aria-label="Last page"
          >
            <ChevronsRight />
          </Button>
        </div>
      </div>
    )
  },
)
DataTablePagination.displayName = 'DataTablePagination'
